import React, {useEffect, useRef, useState} from 'react';
import {useParams, useHistory, Link} from 'react-router-dom';
import ReactSelect from 'react-select';

import {Flow, FlowFetchResponse} from '../_services/Flow';
import {getSearchFilters} from '../_helpers/search';
import {DataTableColumn, ExportEntriesState, Field} from '../types';

import {DataTable} from '../components/DataTable';
import {LoadingIndicator} from '../components/LoadingIndicator';
import {initialFormState, WorkflowSearchForm, WorkflowSearchFormState} from '../components/WorkflowSearchForm';
import {Paginator} from '../components/Paginator';
import {CustomizeColumnsModal, SearchAvailableColumn} from '../components/CustomizeColumnsModal';
import {ExportEntriesModal} from '../components/ExportEntriesModal';
import {WorkflowSavedSearch} from '../components/WorkflowSavedSearch';
import {cellRenderers} from '../_helpers/datatable-cellrenderers';
import {User} from '../_services/User';
import {Cell} from '../components/Cell';
import moment from 'moment';

type ListPageProps = {};

type SearchResultsData = {
    entries: any;
    columns: DataTableColumn[];
    total_count: number;
    page_size: number;
    page_count: number;
    page: number;
    availableColumns: SearchAvailableColumn[];
};

const initialExportEntriesState: ExportEntriesState = {
    show: false,
    exportId: '',
    errorMessage: '',
};

export const List: React.FunctionComponent<ListPageProps> = ({}) => {
    const history = useHistory();
    const {workflowId, parentWorkflowId} = useParams();

    const [workflowTitle, setWorkflowTitle] = useState<string>('');
    const [parentWorkflowTitle, setParentWorkflowTitle] = useState<string>('');
    const [subForms, setSubForms] = useState<any>([]);
    const [searchFilters, setSearchFilters] = useState<any>({
        workflow: workflowId,
        show_step_assignees: User.getInstance().getShowStepAssigneesFilter(true)
    });
    const [searchResults, setSearchResults] = useState<SearchResultsData>({} as SearchResultsData);
    const [searchIsLoading, setInboxIsLoading]: any = useState(false);
    const [searchLoadingErrorMessage, setInboxLoadingErrorMessage]: any = useState('');
    const [showCustomizeColumnsModal, setShowCustomizeColumnsModal] = useState<boolean>(false);
    const [displayColumns, setDisplayColumns] = useState<DataTableColumn[]>([]);
    const [exportEntries, setExportEntries] = useState<ExportEntriesState>({...initialExportEntriesState});
    const [isShowingExportButtonMenu, setIsShowingExportButtonMenu] = useState<boolean>(false);

    const [searchFormState, setSearchFormState] = useState<any>({...initialFormState});
    const [searchIsDirty, setSearchIsDirty] = useState<boolean>(false);

    const exportButtonRef = useRef(null);
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            // @ts-ignore
            if (exportButtonRef?.current && !exportButtonRef.current.contains(event.target)) {
                setIsShowingExportButtonMenu(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [exportButtonRef]);

    useEffect(() => {
        setInboxIsLoading(true);
        setInboxLoadingErrorMessage('');

        Flow.getInstance().search(false, searchFilters)
            .then((response: FlowFetchResponse) => {
                if (response.ok && response.json) {
                    setInboxIsLoading(false);
                    setSearchResults(response.json);

                    // default display columns
                    if (displayColumns.length === 0) {
                        setDisplayColumns(response.json.columns);
                    }
                } else {
                    setInboxLoadingErrorMessage(response.json.message || 'Error performing search');
                }
                setInboxIsLoading(false);
            })
            .catch((error: any) => {
                setInboxLoadingErrorMessage(error || 'Exception performing search');
                setInboxIsLoading(false);
            });

    }, [searchFilters]);

    useEffect(() => {
        Flow.getInstance().getForm(workflowId)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setSubForms(
                        response.json.form.fields
                            .filter((field: Field) => field.type === 'form' && field.showInWorkflowSearch)
                            .map((field: Field) => ({
                                name: field.label,
                                id: field.gpnfForm
                            }))
                    );
                    setWorkflowTitle(response.json.form.title);
                } else {
                    throw Error(response?.json?.message || 'Error getting Workflow');
                }
            })
            .catch((error: any) => {
                console.log(error);
            });
    }, [workflowId]);

    useEffect(() => {
        if (!parentWorkflowId) return;

        Flow.getInstance().getForm(parentWorkflowId)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setParentWorkflowTitle(response.json.form.title);
                } else {
                    throw Error(response?.json?.message || 'Error getting Workflow');
                }
            })
            .catch((error: any) => {
                console.log(error);
            });
    }, [parentWorkflowId]);

    const search = (formValues: any) => {
        const searchFilters = Object.keys(formValues).reduce(getSearchFilters(formValues), {});
        setSearchFilters({...searchFilters, workflow: workflowId, page: 1});
    };

    const handleExport = (onlySelectedColumns = false) => {
        setExportEntries(() => ({
            ...initialExportEntriesState,
            show: true,
        }));

        Flow.getInstance().export(searchFilters, onlySelectedColumns ? displayColumns : null)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setExportEntries((state: ExportEntriesState) => ({
                        ...state,
                        exportId: response.json.exportId
                    }));
                } else {
                    throw Error(response.json.message || 'Error exporting records. Close modal and try again');
                }
            })
            .catch((error: any) => {
                setExportEntries((state: ExportEntriesState) => ({
                    ...state,
                    errorMessage: error?.message || error || 'Error exporting records. Close modal and try again'
                }));
            });
    };

    return (
        <div>
            {parentWorkflowId &&
            <div>
                <button type="button"
                        className="btn btn-link"
                        onClick={() => {
                            history.push('/list/' + parentWorkflowId);
                            setSearchFilters({workflow: parentWorkflowId});
                            setDisplayColumns([]);
                        }}
                >Back {parentWorkflowTitle && `to ${parentWorkflowTitle}`}
                </button>
            </div>
            }

            <div className="d-flex pb-2">
                <h1 className="abb-header flex-grow-1">Search {workflowTitle}</h1>
                {workflowId &&
                <WorkflowSavedSearch
                    workflowId={workflowId}
                    displayColumns={displayColumns}
                    formState={searchFormState}
                    setFormState={(state: WorkflowSearchFormState) => {
                        setSearchFormState(state)
                    }}
                    onSearch={(state: WorkflowSearchFormState, columns: any) => {
                        columns && setDisplayColumns(columns);
                        search(state);
                    }}
                    initialFormState={initialFormState}
                    isDirty={searchIsDirty}
                    setIsDirty={setSearchIsDirty}
                />
                }
            </div>

            <WorkflowSearchForm
                selectedWorkflowId={workflowId}
                onSearch={search}
                onChange={() => {
                    setSearchIsDirty(true);
                }}
                formState={searchFormState}
                setFormState={(state: WorkflowSearchFormState) => {
                    setSearchFormState(state);
                }}
            />

            {searchLoadingErrorMessage && <p className="alert alert-danger">{searchLoadingErrorMessage}</p>}
            {searchIsLoading && <LoadingIndicator/>}

            {searchResults.entries &&
            <div>
                <div className="pt-4 pb-2 row">
                    <div className="col-4">
                        {subForms && subForms.length > 0 &&
                        <ReactSelect
                            placeholder="Select a SubForm"
                            isSearchable={true}
                            isClearable={true}
                            options={subForms.map((subForm: any) => ({
                                value: subForm.id,
                                label: subForm.name
                            }))}
                            onChange={(option: any) => {
                                history.push('/list/' + option.value + '/' + workflowId);
                                setSearchFilters({workflow: option.value});
                                setDisplayColumns([]);
                            }}
                        />
                        }
                    </div>
                    <div className="col-8 text-right">
                        <button type="button"
                                className="btn btn-link"
                                onClick={() => {
                                    setShowCustomizeColumnsModal(true);
                                }}
                        >Customize Columns
                        </button>
                    </div>
                </div>

                <DataTable
                    id="search-table"
                    striped={true}
                    responsive={true}
                    classNames="table-borderless"
                    headings={[
                        ...displayColumns
                    ]}
                    rows={searchResults.entries}
                    cellRenderers={{
                        ...cellRenderers,
                        date_created: (id: any, value: any) => (
                            <Cell
                                key={id}
                                content={<span style={{whiteSpace: 'nowrap'}}>{value ? moment(value).format('M/D/YYYY  H:m a') : value}</span>}
                                classNames={`cell-${id} cell-id`}
                            />
                        ),
                        workflow_timestamp: (id: any, value: any) => (
                            <Cell
                                key={id}
                                content={<span style={{whiteSpace: 'nowrap'}}>{value ? moment(value).format('M/D/YYYY  H:m a') : value}</span>}
                                classNames={`cell-${id} cell-id`}
                            />
                        ),
                    }}
                >
                    {searchResults.entries.length > 0 &&
                    <div className="d-flex justify-content-between align-items-center">

                        <div className="btn-group dropup"
                             ref={exportButtonRef}>
                            <button type="button"
                                    className="btn btn-primary"
                                    onClick={() => {
                                        handleExport(false);
                                        setIsShowingExportButtonMenu(false);
                                    }}
                            >Export All</button>
                            <button type="button"
                                    className="btn btn-primary dropdown-toggle dropdown-toggle-split"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                    onClick={() => {
                                        setIsShowingExportButtonMenu(!isShowingExportButtonMenu);
                                    }}
                            >
                                <span className="sr-only">Toggle Dropdown</span>
                            </button>
                            {isShowingExportButtonMenu &&
                            <div className="dropdown-menu show">
                                <a className="dropdown-item"
                                   href="#"
                                   onClick={(e) => {
                                       e.stopPropagation();
                                       e.preventDefault();

                                       handleExport(true);
                                       setIsShowingExportButtonMenu(false);
                                   }}
                                >Export Only Selected Columns</a>
                            </div>
                            }
                        </div>

                        <Paginator currentPage={searchResults.page}
                                   rowsPerPage={searchResults.page_size}
                                   numberOfPages={searchResults.page_count}
                                   numberOfRecords={searchResults.total_count}
                                   onChange={(page: number, recordsPerPage: number) => {
                                       setSearchFilters({...searchFilters, page, page_size: recordsPerPage});
                                       setSearchResults({...searchResults, entries: []});
                                   }}
                        />
                    </div>
                    }
                </DataTable>

                <p className="mt-3 non-exportable-column"><em>* These fields are not available for export and may be replaced with single value columns</em></p>

                {exportEntries.show &&
                <ExportEntriesModal
                    exportId={exportEntries.exportId}
                    errorMessage={exportEntries.errorMessage}
                    close={() => {
                        setExportEntries({
                            ...initialExportEntriesState
                        });
                    }}
                />
                }
                {showCustomizeColumnsModal &&
                <CustomizeColumnsModal
                    setShow={setShowCustomizeColumnsModal}
                    availableColumns={searchResults.availableColumns}
                    selectedColumns={displayColumns.map((col: any) => col.prop)}
                    onSave={(columns: DataTableColumn[]) => {
                        setDisplayColumns(columns);
                        setSearchIsDirty(true);
                    }}
                />
                }
            </div>
            }
        </div>
    );
};